import React from "react";
import { localizedPath, useLang } from "@/lib/i18n";
import { useSettings } from "@/lib/SettingsContext";
import { useLocation, Link } from "react-router-dom";
import { Mail } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";

export default function Footer() {
  const { t, lang, pick } = useLang();
  const { settings } = useSettings();
  const loc = useLocation();
  if (loc.pathname.startsWith("/admin")) return null;
  const s = settings || {};
  const brand = pick(s, "brand_name") || "Mindovia";
  const footerNote = pick(s, "footer_note") || t("footer.note");
  const supportEmail = s.support_email || "";
  const supportText = pick(s, "support_contact_text") || (lang === "id" ? "Butuh bantuan? Hubungi tim dukungan kami." : "Need help? Get in touch with our support team.");

  const legalLinks = [
    { to: "/legal/privacy", label_id: "Kebijakan Privasi", label_en: "Privacy Policy", tid: "footer-privacy-link" },
    { to: "/legal/terms", label_id: "Syarat & Ketentuan", label_en: "Terms & Conditions", tid: "footer-terms-link" },
    { to: "/legal/refund", label_id: "Kebijakan Refund", label_en: "Refund Policy", tid: "footer-refund-link" },
    { to: "/legal/disclaimer", label_id: "Disclaimer", label_en: "Disclaimer", tid: "footer-disclaimer-link" },
  ];

  return (
    <footer className="border-t border-border/60 mt-24 bg-cream-50/40" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 grid gap-10 md:grid-cols-4">
        {/* Brand + note */}
        <div className="md:col-span-2">
          <div className="font-display font-semibold text-lg" data-testid="footer-brand">{brand}</div>
          <div className="text-sm text-muted-foreground max-w-md whitespace-pre-line mt-2 break-words">{footerNote}</div>
          <div className="mt-4"><SocialLinks /></div>
        </div>

        {/* Site links */}
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "id" ? "Tautan" : "Links"}</div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to={localizedPath("/", lang)} className="hover:text-primary" data-testid="footer-about-link">
                {lang === "id" ? `Tentang ${brand}` : `About ${brand}`}
              </Link>
            </li>
            <li>
              <Link to="/feedback" className="hover:text-primary" data-testid="footer-feedback-link">
                {lang === "id" ? "Umpan Balik" : "Feedback"}
              </Link>
            </li>
            {legalLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-primary" data-testid={l.tid}>
                  {lang === "id" ? l.label_id : l.label_en}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "id" ? "Dukungan" : "Support"}</div>
          <div className="text-sm text-muted-foreground mb-2 break-words" data-testid="footer-support-text">{supportText}</div>
          {supportEmail ? (
            <a
              href={`mailto:${supportEmail}`}
              className="inline-flex items-center gap-1.5 text-sm text-terracotta-600 hover:text-terracotta-700 break-all"
              data-testid="footer-support-email"
            >
              <Mail className="w-3.5 h-3.5" /> {supportEmail}
            </a>
          ) : (
            <div className="text-xs text-muted-foreground italic" data-testid="footer-support-empty">
              {lang === "id" ? "(Email dukungan belum dikonfigurasi)" : "(Support email not configured)"}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-6 py-4 text-xs text-muted-foreground flex flex-wrap items-center gap-2 justify-between">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} {brand} — {lang === "id" ? "Alat edukasi non-diagnostik." : "Non-diagnostic educational tool."}</span>
        </div>
      </div>
    </footer>
  );
}
